import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Divider,
  alpha
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { format, parseISO, isWithinInterval, addDays, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { getBrusselsDate } from '../utils/dateUtils';

const Dashboard = () => {
  const [schedules, setSchedules] = useState([]);
  const [refusedSchedules, setRefusedSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, activeCompany } = useContext(AuthContext);
  const isAdmin = activeCompany?.CompanyUser?.isAdmin === true;

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !activeCompany) return;
      setLoading(true);
      try {
        // Fetch user's schedules
        const today = format(getBrusselsDate(), 'yyyy-MM-dd');
        const userSchedulesPromise = axiosInstance.get(`/schedule/me?startDate=${today}`);

        // Fetch refused schedules for the company if admin
        const refusedSchedulesPromise = isAdmin
          ? axiosInstance.get(`/schedule?companyId=${activeCompany.id}&status=refuse&startDate=${today}`)
          : Promise.resolve({ data: [] });

        const [userSchedulesRes, refusedSchedulesRes] = await Promise.all([
          userSchedulesPromise,
          refusedSchedulesPromise
        ]);

        setSchedules(userSchedulesRes.data);
        setRefusedSchedules(refusedSchedulesRes.data);

      } catch (err) {
        setError('Erreur lors du chargement des données du tableau de bord.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, activeCompany, isAdmin]);

  const handleScheduleStatusUpdate = async (scheduleId, newStatus) => {
    try {
      await axiosInstance.put(`/schedule/me/${scheduleId}/status`, { status: newStatus });
      // Met à jour l'état local pour refléter le changement immédiatement
      setSchedules(prevSchedules =>
        prevSchedules.map(s =>
          s.id === scheduleId ? { ...s, confirmationStatus: newStatus } : s
        )
      );
    } catch (err) {
      setError('Erreur lors de la mise à jour de la demande.');
    }
  };

  const today = startOfDay(getBrusselsDate());
  const nextSevenDaysInterval = {
    start: today,
    end: addDays(today, 7)
  };

  const pendingSchedules = schedules.filter(s => s.confirmationStatus === 'wait');
  const upcomingSchedules = schedules
    .filter(s => // On inclut aussi les prestations en attente
      ['confirm', 'force', 'wait'].includes(s.confirmationStatus) &&
      isWithinInterval(parseISO(s.date), nextSevenDaysInterval) 
    ).sort((a, b) => new Date(a.date) - new Date(b.date));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Bonjour, {currentUser?.firstName} !
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Boutons d'accès rapide */}
      <Grid container spacing={2} sx={{ mb: 4 }} size={12}>
        <Grid size={{xs: 12, md: 6}}>
          <Button
            component={RouterLink}
            to="/availability"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<EventAvailableIcon />}
            sx={{ py: 2, fontSize: {xs: '.8rem', md: '0.9rem', lg: '1rem' } }}
          >
            Gérer mes disponibilités
          </Button>
        </Grid>
        <Grid size={{xs: 12, md: 6}}>
          <Button
            component={RouterLink}
            to="/schedule"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<ScheduleIcon />}
            sx={{ py: 2, fontSize: {xs: '.8rem', md: '0.9rem', lg: '1rem' } }}
          >
            Consulter mon horaire
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Demandes en attente */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>Demandes en attente</Typography>
          {pendingSchedules.length > 0 ? (
            <Grid container spacing={2}>
              {pendingSchedules.map(schedule => {
                const scheduleDate = parseISO(schedule.date);
                return (
                  <Grid key={schedule.id} size={{xs: 12, sm: 6, md: 6, lg: 4}}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Box sx={{ textAlign: 'center', flexGrow: 1 }}>
                        <Typography variant="body1" textTransform="capitalize">
                          {format(scheduleDate, 'eeee', { locale: fr })}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                          {format(scheduleDate, 'd', { locale: fr })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(scheduleDate, 'MMMM yyyy', { locale: fr })}
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="subtitle1" fontWeight="bold">{schedule.Task.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {schedule.startTime.substring(0, 5)} - {schedule.endTime.substring(0, 5)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button fullWidth variant="outlined" color="error" onClick={() => handleScheduleStatusUpdate(schedule.id, 'refuse')}>Refuser</Button>
                        <Button fullWidth variant="contained" color="success" onClick={() => handleScheduleStatusUpdate(schedule.id, 'confirm')}>Confirmer</Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">Aucune demande en attente.</Typography>
          )}
        </Grid>

        {/* Demandes refusées (Admin only) */}
        {isAdmin && (
          <Grid size={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Demandes refusées</Typography>
              <Divider sx={{ mb: 1 }} />
              {refusedSchedules.length > 0 ? (
                <List dense>
                  {refusedSchedules.map(schedule => (
                    <ListItem key={schedule.id}>
                      <ListItemIcon><ThumbDownOffAltIcon color="error" /></ListItemIcon>
                      <ListItemText
                        primary={`${schedule.User.firstName} ${schedule.User.lastName}`}
                        secondary={
                          `A refusé la tâche "${schedule.Task.name}" du ${format(parseISO(schedule.date), 'd MMMM', { locale: fr })}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">Aucune demande refusée pour les jours à venir.</Typography>
              )}
            </Paper>
          </Grid>
        )}

        {/* Prochaines prestations */}
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Prestations des 7 prochains jours</Typography>
            <Divider sx={{ mb: 1 }} />
            {upcomingSchedules.length > 0 ? (
              <List dense>
                {upcomingSchedules.map(schedule => (
                  <ListItem
                    key={schedule.id}
                    sx={{
                      // Applique un fond orange pour les prestations en attente
                      ...(schedule.confirmationStatus === 'wait' && { backgroundColor: alpha('#ff9800', 0.1) }),
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <ListItemIcon><EventIcon color="primary" /></ListItemIcon>
                    <ListItemText
                      primary={schedule.Task.name}
                      secondary={`Le ${format(parseISO(schedule.date), 'eeee d MMMM', { locale: fr })} à ${schedule.startTime.substring(0, 5)}`}
                    />
                    {/* Ajoute une pastille de statut pour plus de clarté */}
                    {schedule.confirmationStatus === 'wait' && (
                      <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                        À confirmer
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">Aucune prestation à venir.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
